import { customField } from '@libs/domain';
import type { PageProps } from '@ps-smart-cp/ppfw-client';
import {
  ItemArray,
  Template,
  useGuideTab,
  useItemName,
  useLocalizedContents,
  useTry,
  useValidator,
  withRerender,
} from '@ps-smart-cp/ppfw-client';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useCustomFieldSettings } from '../../context';
import { CustomFieldLedgerFieldSet, CustomFieldSettingFooterButton } from './components';
import { customFieldLedgerSettingFieldConfig } from './item-config';
import type { CustomFieldLedgerSettingDetail } from './models';
import { CustomFieldLedgerSettingRaw } from './models';
import { registerLedgerSettings } from './repositories';

function PageComponent({ rerender, rerenderContentsOnly }: PageProps) {
  const BASIC_INFO_SETTING_PATH = '/fa/custom-field-setting/basic-info';

  const getName = useItemName();
  const { basicSetting, ledgerSetting, refetch } = useCustomFieldSettings();
  const [ledgerSettings, setLedgerSettings] = useState(ledgerSetting);
  const { setInfo } = useGuideTab();
  const { errorFields, validateThenTryRequest } = useTry();
  const { msg } = useLocalizedContents();
  const navigateToBasicSetting = useNavigate();
  const validator = useValidator();

  const selectBoxOptions = basicSetting
    .filter((o) => o.isUsed)
    .map((o) => {
      return { displayValue: o.displayName, optionValue: o.customFieldKey.toString() };
    });

  const onRegisterClick = validateThenTryRequest(
    () =>
      validator
        .with(
          { ledgerSettings },
          {
            ledgerSettings: ItemArray.of<CustomFieldLedgerSettingDetail>(customFieldLedgerSettingFieldConfig),
          },
        )
        .execute(),
    async () => {
      await registerLedgerSettings(
        ledgerSettings.map((setting) => CustomFieldLedgerSettingRaw.fromModel(setting)),
      );
      await refetch();
      setInfo([msg.ico13002]);
      rerenderContentsOnly();
    },
  );

  return (
    <Template
      title={getName(customField.titleNames.ledgerConnectionSetting)}
      footerButtonGroup={
        <CustomFieldSettingFooterButton
          navigateButtonItemName={customField.buttonNames.toBasicInfoSetting}
          onNavigate={() => navigateToBasicSetting(BASIC_INFO_SETTING_PATH)}
          onRegister={onRegisterClick}
          onReset={rerender}
        />
      }
    >
      {ledgerSettings.map((setting) => {
        return (
          <CustomFieldLedgerFieldSet
            key={setting.ledger}
            ledger={setting.ledger}
            selectBoxOptions={selectBoxOptions}
            settingDetails={setting.detail}
            setSettings={(changed) =>
              setLedgerSettings(
                ledgerSettings.map((checkSetting) => {
                  if (checkSetting.ledger === setting.ledger) {
                    return { ...checkSetting, detail: changed };
                  } else {
                    return checkSetting;
                  }
                }),
              )
            }
            errorFields={errorFields}
          />
        );
      })}
    </Template>
  );
}

const LedgerConnectionSetting = withRerender(PageComponent, 'LedgerConnectionSetting');

export { LedgerConnectionSetting };
